import React from "react";
import { Chat } from "../../../../components/icons/svg/outline";

/**
 * TicketCommentsSection Component
 * Reusable comments section for ticket forms with character limit and counter
 * 
 * @param {Object} data - Form data object
 * @param {Function} onChange - Callback when data changes
 * @param {boolean} readOnly - Whether the section is read-only
 * @param {string} title - Section title (default: "Comments")
 * @param {React.Component} icon - Icon component to display (default: ChatBubbleBottomCenterText)
 * @param {string} fieldName - Field name in data object (default: "comments")
 * @param {string} placeholder - Placeholder text
 * @param {number} maxLength - Character limit (default: 500)
 * @param {number} rows - Number of textarea rows (default: 3)
 * @param {boolean} showBorder - Whether to show bottom border (default: true)
 * @returns {JSX.Element}
 */
const TicketCommentsSection = ({
  data,
  onChange,
  readOnly = false,
  title = "Comments",
  icon: Icon = Chat,
  fieldName = "comments",
  placeholder = "Add any additional comments or notes...",
  maxLength = 500,
  rows = 3,
  showBorder = true,
}) => {
  const currentLength = data?.[fieldName]?.length || 0;
  const remainingChars = maxLength - currentLength;

  return (
    <div className={showBorder ? "border-b pb-4" : ""}>
      {title && (
        <h2 className="text-lg font-semibold text-gray-700 mb-2 flex items-center gap-2">
          {Icon && <Icon className="w-5 h-5 text-emerald-800" />}
          {title} {readOnly && "(Read Only)"}
        </h2>
      )}
      {readOnly ? (
        <div className="border rounded p-4 bg-gray-50">
          <p className="text-gray-700 whitespace-pre-wrap">
            {data?.[fieldName] || "No comments"}
          </p>
        </div>
      ) : (
        <>
          <textarea
            className="w-full border rounded p-2 caret-black focus:ring-[1px] focus:ring-gray-500 focus:outline-none"
            rows={rows}
            placeholder={placeholder}
            value={data?.[fieldName] || ""}
            maxLength={maxLength}
            onChange={(e) =>
              onChange?.({ ...data, [fieldName]: e.target.value })
            }
          />
          <div className="text-xs text-gray-500 mt-1 text-right">
            {remainingChars} characters remaining
          </div>
        </>
      )}
    </div>
  );
};

export default TicketCommentsSection;
